import {Fragment, useEffect, useRef, useState} from 'react'
import type {Repository} from '@github-ui/current-repository'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import type {HomeRoutePayload, Ruleset} from '../types/rules-types'
import {Blankslate} from '../components/Blankslate'
import {BorderBox} from '../components/BorderBox'
import {BranchSelector} from '../components/BranchSelector'
import {Subhead} from '../components/Subhead'
import {RulesetList} from '../components/RulesetList'
import {FlashUpsellBanner, UpsellBanner} from '../components/UpsellBanner'
import {useRelativeNavigation} from '../hooks/use-relative-navigation'
import {NewRulesetButton} from '../components/NewRulesetButton'
import {capitalize} from '../helpers/string'
import {sortSourceTypes} from '../helpers/utils'
import {DismissibleFlashOrToast, type FlashAlert} from '@github-ui/dismissible-flash'
import {useRuleStrings} from '../hooks/use-rule-strings'
import {useUpsellInfoContext} from '../contexts/UpsellInfoContext'

import styles from './OverviewPage.module.css'

export function OverviewPage() {
  const {source, sourceType, rulesets, matchingRulesets, editableRulesets, branch, branchListCacheKey, readOnly} =
    useRoutePayload<HomeRoutePayload>()
  const {resolvePath} = useRelativeNavigation()
  const upsellInfo = useUpsellInfoContext()
  const [flashAlert, setFlashAlert] = useState<FlashAlert>({message: '', variant: 'default'})
  const flashRef = useRef<HTMLDivElement | null>(null)

  const {heading, alphaOrBeta, headingText, rulesetOrPolicy, rulesetsOrPolicies} = useRuleStrings()

  useEffect(() => {
    flashRef.current?.focus()
  }, [flashAlert, flashRef])

  const rulesetsBySource = rulesets.reduce<Record<string, Ruleset[]>>((map, item) => {
    // if a branch/ref is specified, filter based on whether each ruleset is applied
    if (branch && !matchingRulesets.includes(item.id)) {
      return map
    }

    const key = item.source.type
    if (!map[key]) {
      map[key] = []
    }

    map[key].push(item)
    return map
  }, {})

  const inheritedSourceTypes = Object.keys(rulesetsBySource).sort(sortSourceTypes)

  const hasFilteredRulesets = rulesets.length > 0 && inheritedSourceTypes.length === 0

  return (
    <div className="settings-next position-relative" data-hpc>
      <Subhead
        heading={heading}
        alphaOrBeta={alphaOrBeta}
        renderAction={() => {
          return readOnly ? null : rulesets.length > 0 ? (
            <NewRulesetButton
              rulesetsUrl={resolvePath('./')}
              setFlashAlert={setFlashAlert}
              className={styles.NewRulesetButton}
            />
          ) : null
        }}
      />
      <DismissibleFlashOrToast flashAlert={flashAlert} setFlashAlert={setFlashAlert} ref={flashRef} />
      <div className="d-flex flex-column gap-3">
        {upsellInfo.rulesets.cta.visible && inheritedSourceTypes.length > 0 && (
          <FlashUpsellBanner ctaType="rulesets" sourceType={sourceType} />
        )}
        {sourceType === 'repository' && rulesets.length > 0 ? (
          <div>
            <BranchSelector gitRef={branch} repo={source as Repository} branchListCacheKey={branchListCacheKey} />
          </div>
        ) : null}
        {inheritedSourceTypes.length > 0 ? (
          inheritedSourceTypes.map(key => (
            <Fragment key={key}>
              <BorderBox
                name={`${capitalize(key)} rulesets`}
                showHeader={inheritedSourceTypes.length > 1 || !inheritedSourceTypes.includes(capitalize(sourceType))}
                renderCreateButton={() => {
                  // Don't show managed by if the source type is the same as the group's type
                  if (sourceType === key.toLowerCase()) {
                    return null
                  }

                  const ownedByName = rulesetsBySource[key]![0]!.source.name
                  return <div className="text-small color-fg-muted mt-1">Managed by {ownedByName}</div>
                }}
              >
                <RulesetList
                  rulesets={rulesetsBySource[key]!}
                  sourceType={sourceType}
                  source={source}
                  readOnly={readOnly}
                  setFlashAlert={setFlashAlert}
                />
              </BorderBox>

              {!readOnly &&
                key.toLowerCase() !== sourceType &&
                editableRulesets.includes(rulesetsBySource[key]![0]!.id) && (
                  <div className="text-small">
                    <span className="text-bold">Tip:</span>{' '}
                    <span className="color-fg-muted">
                      As a {rulesetsBySource[key]![0]?.source.name} admin, you can
                      <a href={rulesetsBySource[key]![0]?.source.url}>
                        {' '}
                        manage {source.name} rulesets in {key.toLocaleLowerCase()} settings.
                      </a>
                    </span>
                  </div>
                )}
            </Fragment>
          ))
        ) : (
          <>
            {hasFilteredRulesets ? (
              <Blankslate heading={`No ${rulesetsOrPolicies} matched your search`}>
                {`Try expanding your search or creating a new ${rulesetOrPolicy}`}
              </Blankslate>
            ) : (
              <UpsellBanner
                headingText={headingText}
                withBorder={false}
                showNewRulesetButton={!readOnly}
                infoVariant="two"
                sourceType={sourceType}
                ctaType="rulesets"
                setFlashAlert={setFlashAlert}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

try{ OverviewPage.displayName ||= 'OverviewPage' } catch {}